import { Box, Container, Typography } from "@mui/material";
import clsx from "clsx";
import { observer } from "mobx-react-lite";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import RouterConstants from "../../../../constants/RouterConstants.ts";
import { ORG_SUBSCRIPTION_PLANS } from "../../../../constants/billing.ts";
import useSecureNavigate from "../../../../hooks/useSecureNavigate.tsx";
import { useAppStore } from "../../../../hooks/useStores.tsx";
import { usePostHog } from "../../../../hooks/usePostHog.tsx";
import { OrgSubscriptionPlan } from "../../../../models/billing.ts";
import BriaButton from "../../../common/BriaButton/BriaButton.tsx";
import PricingCard from "../PricingCard/PricingCard.tsx";
import { usePricingFeatureContent } from "../hooks/usePricingFeatureContent.ts";
import { POSTHOG_EVENTS } from "../../../../constants/PostHogEvents";
import styles from "./PricingContent.module.scss";

interface Features {
	[key: string]: {
		title: string;
		tooltip: string;
		starter: string;
		pro: string;
		enterprise: string;
		tooltipLink?: string;
		showActionsPricingTooltip?: boolean;
	};
}

interface IPricingContentProps {
	onPlanClick?: (plan: OrgSubscriptionPlan) => void;
	isPublicPage?: boolean;
	onClose?: () => void;
	className?: string;
}

const PricingContent = ({ onPlanClick, isPublicPage = false, onClose, className }: IPricingContentProps) => {
	const { t } = useTranslation("translation", { keyPrefix: "pricingFlow.pricingPopup" });
	const { authStore, uiStore, pricingStore } = useAppStore();
	const { trackEvent } = usePostHog();
	const navigate = useSecureNavigate();
	const [selectedFeature, setSelectedFeature] = useState<string | null>(null);
	const [maxExpandedHeight, setMaxExpandedHeight] = useState<number>(0);
	const featureContentRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

	const { getFeatureContent } = usePricingFeatureContent();

	const features: Features = t("pricingFeatures", {
		returnObjects: true,
	});

	const pricingCards = t("pricingCards", {
		returnObjects: true,
	});

	// Track pricing page viewed when component loads
	useEffect(() => {
		const source = isPublicPage ? 'public_page' : 'dashboard';
		
		trackEvent(POSTHOG_EVENTS.PRICING_PAGE_VIEWED, {
			source: source,
		});
	}, [isPublicPage, trackEvent]);

	// Calculate max height for expanded features
	useEffect(() => {
		if (selectedFeature) {
			setTimeout(() => {
				const heights: number[] = [];
				Object.keys(pricingCards).forEach((planKey) => {
					const content = getFeatureContent(planKey, selectedFeature);
					if (content.features && content.features.length > 0) {
						const ref = featureContentRefs.current[`${planKey}-${selectedFeature}`];
						if (ref) {
							// Get the actual content height without any padding/margins
							const computedStyle = window.getComputedStyle(ref);
							const contentHeight =
								ref.scrollHeight -
								parseInt(computedStyle.paddingTop) -
								parseInt(computedStyle.paddingBottom);
							heights.push(contentHeight);
						}
					}
				});

				if (heights.length > 0) {
					const maxHeight = Math.max(...heights);
					setMaxExpandedHeight(maxHeight);
				}
			}, 0);
		} else {
			setMaxExpandedHeight(0);
		}
	}, [selectedFeature, pricingCards]);

	const handleFeatureClick = (featureId: string | null) => {
		setSelectedFeature(featureId);
		
		// Track plan comparison viewed when feature is selected
		if (featureId) {
			const featureTitle = features[featureId]?.title || featureId;
			
			trackEvent(POSTHOG_EVENTS.PLAN_COMPARISON_VIEWED, {
				feature: featureTitle,
			});
		}
	};

	const trackContactSalesClick = (planInterest: string) => {
		trackEvent(POSTHOG_EVENTS.CONTACT_SALES_CLICKED, {
			plan_interest: planInterest,
		});
	};

	const handleButtonClick = (planKey: string) => {
		const currentPlanName = authStore.orgSubscription?.plan_name;

		switch (planKey) {
			case "freeTrial":
				// Free Trial logic based on current subscription
				if (
					currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name ||
					currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name
				) {
					// User has paid plan - no action for free trial
					return;
				} else if (isPublicPage || !authStore.user) {
					navigate(
						`${RouterConstants.LOGIN.path}?continueUrl=${encodeURIComponent(
							RouterConstants.AUTH_KEYS.fullPath,
						)}`,
					);
				} else if (authStore.userOrganizations && authStore.userOrganizations.length > 0) {
					navigate(RouterConstants.AUTH_KEYS.fullPath);
					onClose?.();
				} else {
					uiStore.showDialog("CreateOrganizationDialog");
					onClose?.();
				}
				break;
			case "selfService":
				// PAYG (starter) logic based on current subscription
				if (currentPlanName === ORG_SUBSCRIPTION_PLANS.starter.name) {
					// User is already on starter plan - show manage subscription (customer portal)
					navigate(RouterConstants.SUBSCRIPTION.fullPath);
					onClose?.();
				} else if (
					currentPlanName === ORG_SUBSCRIPTION_PLANS.pro.name ||
					currentPlanName === ORG_SUBSCRIPTION_PLANS.enterprise.name ||
					currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name ||
					currentPlanName === ORG_SUBSCRIPTION_PLANS.aws.name
				) {
					// User has custom plan - no action for PAYG
					return;
				} else if (
					isPublicPage &&
					(!authStore.user || !authStore.userOrganizations || authStore.userOrganizations.length === 0)
				) {
					// On public page: redirect to signup for both unauthenticated users and authenticated users without orgs
					navigate(`${RouterConstants.SIGNUP.path}/?pricing=true&selectedPlan=starter`);
				} else if (!authStore.userOrganizations || authStore.userOrganizations.length === 0) {
					// Console users without org: create org first, then proceed with upgrade
					// Set the selected plan in pricing store so it will be processed after org creation
					pricingStore.setProperty("selectedPlan", ORG_SUBSCRIPTION_PLANS.starter);
					// Set the destination route for after org creation
					localStorage.setItem("destination_route", RouterConstants.AUTH_KEYS.fullPath + "?pricing=true");
					uiStore.showDialog("CreateOrganizationDialog");
					onClose?.();
				} else if (onPlanClick) {
					onPlanClick(ORG_SUBSCRIPTION_PLANS.starter);
				}
				break;
			case "briaService":
				// Custom plan logic based on current subscription
				if (currentPlanName === ORG_SUBSCRIPTION_PLANS.custom.name) {
					// User has custom plan - show manage plan (contact us)
					trackContactSalesClick("custom");
					window.open("https://bria.ai/contact-us", "_blank");
				} else {
					// Default behavior for non-custom plans
					trackContactSalesClick("enterprise");
					window.open("https://bria.ai/contact-us", "_blank");
				}
				break;
			default:
				break;
		}
	};

	return (
		<Container maxWidth="xl" className={className}>
			<Box className={styles.pricingHeaderContainer}>
				<Typography className={styles.pricingHeader} variant="h2">
					{t("briaAi")} {t("title")}
				</Typography>
			</Box>
			<Box className={styles.pricingContainer}>
				<Box className={styles.featuresContainer}>
					{Object.entries(features).map(([_key, feature]: [string, any]) => (
						<BriaButton
							className={clsx(styles.featureBtn, {
								[styles.selectedFeature]: selectedFeature === feature.id,
							})}
							buttonType="textMedium"
							onClick={() => {
								handleFeatureClick(feature.id);
								setMaxExpandedHeight(0);
							}}
							style={{
								marginBottom:
									maxExpandedHeight > 0 && selectedFeature === feature.id
										? `${maxExpandedHeight - 12}px`
										: "0px",
							}}
						>
							{feature.title}
						</BriaButton>
					))}
				</Box>
				{Object.entries(pricingCards).map(([key, card]: [string, any]) => (
					<PricingCard
						key={key}
						{...card}
						className={clsx(styles.pricingCard, {
							[styles.briaService]: key === "briaService",
						})}
						buttonType={key === "briaService" ? "primaryMedium" : "tertiaryMedium"}
						planKey={key}
						features={features}
						getFeatureContent={getFeatureContent}
						selectedFeature={selectedFeature}
						onFeatureClick={handleFeatureClick}
						maxExpandedHeight={maxExpandedHeight}
						featureContentRefs={featureContentRefs}
						onButtonClick={() => handleButtonClick(key)}
					/>
				))}
			</Box>
		</Container>
	);
};

export default observer(PricingContent);
